import isEqual from 'lodash/isEqual';
import Http from '@/services/http';
import UserService from '@/services/user';
import { level } from '@/domain/enums/user';
import { permissionGroups, allPermissions } from 'sigma-mappings';

const urls = {
  getUsersToCopy: '/usuario/lookup',
  getUserPermissions: '/usuario/{userId}/permissoes',
  getPermissionLevels: '/Permissao',
};

/**
 * Aplica as permissões em um destino levando em consideração se o usuário logado pode
 * definir aquela permissão.
 * @param {String[]} permissionsToApply Array de permissões que serão aplicadas.
 * @param {Object} targetPermissions Objeto que carrega as definições de permissões.
 * @return {Boolean} True se todas as permissões puderam ser aplicadas.
 * False se alguma permissão foi negada devido ao usuário logado não possuí-la.
 */
function applyPermissions(permissionsToApply, targetPermissions) {
  let allApplied = true;
  allPermissions.forEach((permission) => {
    let condition = permissionsToApply.includes(permission);
    if (condition && !UserService.hasPermission(permission)) {
      allApplied = false;
      condition = false;
    }
    // eslint-disable-next-line no-param-reassign
    targetPermissions[permission] = condition;
  });
  return allApplied;
}

export default {
  /**
   * Obtem a lista de usuários que podem ser copiados.
   * @returns {Promise<{id: string, name: string}[]>} Lista de usuários.
   */
  getUsersToCopy() {
    const http = new Http();
    return http.get(urls.getUsersToCopy).then(response => response.data.map(usuario => ({
        id: usuario.id,
        name: usuario.nome,
      })));
  },
  /**
   * Obtem as permissões agrupadas para exibição.
   * @returns {Promise<{name: string, permissions: any[], children?: any[]}[]>} Lista de permissões.
   */
  getGroupedPermissions() {
    return Promise.resolve(permissionGroups);
  },
  /**
   * Obtem as permissões por nível.
   * @returns {Promise<{nome_perfil: string[]}[]>} Lista de permissões agrupadas por nível.
   */
  getPermissionLevels() {
    const http = new Http();
    return http.get(urls.getPermissionLevels).then(({ data }) => ({
      [level.Admin]: data.permissoes.administrador || [],
      [level.Operator]: data.permissoes.operador || [],
      [level.Viewer]: data.permissoes.visualizador || [],
      [level.Custom]: [],
    }));
  },
  /**
   * Aplica as permissões de um usuário de origem.
   * @param {Number} sourceUserId
   * @param {Object} targetPermissions Objeto que carrega as definições de permissões.
   * @return {Promise<Boolean>} True se todas as permissões puderam ser aplicadas.
   * False se alguma permissão foi negada devido ao usuário logado não possuí-la.
   */
  applyUserPermissions(sourceUserId, targetPermissions) {
    const http = new Http();
    return http
      .get(urls.getUserPermissions.replace(/{userId}/g, sourceUserId))
      .then(response => applyPermissions(response.data, targetPermissions));
  },
  /**
   * Aplica as permissões de um nível.
   * @param {String[]} sourceLevelPermissions
   * @param {Object} targetPermissions Objeto que carrega as definições de permissões.
   * @return {Promise<Boolean>} True se todas as permissões puderam ser aplicadas.
   * False se alguma permissão foi negada devido ao usuário logado não possuí-la.
   */
  applyLevelPermissions(sourceLevelPermissions, targetPermissions) {
    return Promise.resolve(
      applyPermissions(sourceLevelPermissions, targetPermissions),
    );
  },
  /**
   * Retorna o perfil que as permissões passadas se encaixam, ou personalizado, se não se encaixar
   * em nenhuma definição.
   * @param {Object} permissionsFlag
   * @param {{perm: string[],}} permissionLevelsList
   * @return {String} Nome do perfil que se enquadra.
   */
  getLevelByPermissions(permissionsFlag, permissionLevelsList) {
    const permissionsArray = this.toPermissionArray(permissionsFlag);
    return (
      Object.keys(permissionLevelsList).find(levelName => this.hasSamePermissions(
          permissionsArray,
          permissionLevelsList[levelName],
        )) || 'custom'
    );
  },
  /**
   * Compara se duas listas de permissões são igual, possuindo as mesmas permissões.
   * @param {String[]} a Lista A
   * @param {String[]} b Lista B
   * @returns {Boolean} Verdadeiro se as duas listas contiverem as mesmas permissões.
   */
  hasSamePermissions(a, b) {
    return isEqual([...a].sort(), [...b].sort());
  },
  /**
   * Converte um objeto de permissões { PER001: true, PER002: false, ...} em uma array
   * de permissões com todas as permissões ativas.
   * @param {Object} permissionObject Objeto de permissões.
   * @returns {String[]} Array com as permissões que estavam habilitadas no objeto.
   */
  toPermissionArray(permissionObject) {
    return allPermissions.filter(permission => permissionObject[permission]);
  },
  /**
   * Converte uma array de permissões em um objeto { PER001: true, PER002: false, ...}
   * de permissões com as permissões de entrada ativas e as demais desativas.
   * @param {String[]} permissionArray Array de permissões.
   * @returns {Object} Objeto com as permissões.
   */
  toPermissionObject(permissionArray) {
    const permissionObject = {};
    const permissionArrayUpper = permissionArray.map(p => p.toUpperCase());
    allPermissions.forEach((permission) => {
      permissionObject[permission] = permissionArrayUpper.includes(permission);
    });
    return permissionObject;
  },
};
